import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-router-dom";
import {Switch} from "antd";
import {Form, Formik} from "formik";
import {inject, observer} from "mobx-react";
import * as qr from "querystring-es3";
// locale
import {schema, initialValues} from "./formAttrs";
import {ReactNumberInput} from "components/ReactNumberInput";
import {InputsCode} from "components/InputsCode";
import {Button} from "components/buttons/Button";
import FormikField from "components/FormikField";
import {ButtonRed} from "components/buttons/ButtonRed";
import {TERMS_AND_CONDITIONS} from "../../../../constants/documents.consts";
import {scrollToBottom} from "helpers/helper";
import {showError} from "helpers/notifications.helper";

const InviteOnBoardingPage = inject("store")(
  observer(({store: {organization, auth}}) => {
    const {t} = useTranslation();

    const {phoneNumber, request_id} = auth;

    const isValidData = !!request_id;
    const [loading, setLoading] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [ifChecked, setIfChecked] = useState(false);
    const location = useLocation();
    const query = qr.parse(location.search.replace("?", ""));
    const joinRequestId = query["id"];

    const handleSubmit = async (value, {setFieldError, setFieldValue}) => {
      const values = {
        ...value,
        companyNumber: query["companyNumber"],
        companyName: query["organizationName"],
        number: phoneNumber,
      };
      const formError = (e) => {
        if (e?.response?.status === 400) {
          const message = e?.response?.data?.error;
          const field = message.includes("ORG_NUMBER")
            ? "companyNumber"
            : message.includes("EMAIL")
            ? "email"
            : null;
          field
            ? setFieldError(field, t(message))
            : message.includes("code provided does not match")
            ? showError(t("LOGIN_SMS_CODE_ERROR"))
            : showError("Unknown validation error");
        } else {
          e?.response?.data?.error
            ? showError(e?.response?.data?.error)
            : showError("Unknown error");
          auth.setToken(null);
        }

        auth.setRequestId(null);
        setFieldValue("code", ["", "", "", ""]);
      };
      const payload = isValidData
        ? {...values, code: values.code.join(""), request_id}
        : {...values};

      try {
        setLoading(true);
        if (isValidData) await auth.sendForm(payload);
        else auth.sendFormForCheck(payload).then(scrollToBottom).catch(formError);
      } catch (e) {
        formError(e);
      } finally {
        setLoading(false);
      }
    };

    const handleReject = () => {
      setRejecting(true);
      organization
        .rejectJoinRequest(joinRequestId, query["email"])
        // usually no token here (not registered yet), so logout() only clears the browser
        .then(() => auth.logout())
        .finally(() => setRejecting(false));
    };

    return (
      <div className="invite-page_info __wrapper">
        <div className="invite-page_info __form">
          <ReactNumberInput phone={phoneNumber} disabled setIsValid={() => undefined} />
          <Formik
            initialValues={{
              ...initialValues,
              email: query["email"],
            }}
            validationSchema={schema}
            onSubmit={handleSubmit}
          >
            {({dirty, isValid, values, setFieldValue}) => {
              const allFieldsFilled =
                !!values.firstName?.trim() &&
                !!values.lastName?.trim() &&
                !!values.email?.trim() &&
                (!isValidData || values.code.every((digit) => digit.length === 1));

              return (
                <Form>
                  <FormikField
                    label={t("LOGIN_PLACEHOLDER_FIRST_NAME")}
                    name="firstName"
                    errorName="firstName"
                    placeholder={t("LOGIN_PLACEHOLDER_FIRST_NAME_")}
                  />
                  <FormikField
                    label={t("LOGIN_PLACEHOLDER_LAST_NAME")}
                    name="lastName"
                    errorName="lastName"
                    placeholder={t("LOGIN_PLACEHOLDER_LAST_NAME")}
                  />
                  <FormikField
                    type="email"
                    label={t("E_MAIL")}
                    name="email"
                    errorName="email"
                    placeholder={t("LOGIN_PLACEHOLDER_EMAIL")}
                  />
                  <FormikField
                    label={t("INVITE_ORGANIZATION_NUMBER")}
                    value={query["companyNumber"]}
                    disabled={true}
                  />
                  <FormikField
                    label={t("INVITE_ORGANIZATION_NAME")}
                    value={query["organizationName"]}
                    disabled={true}
                  />
                  <div className="invite-page_info __switch">
                    <Switch
                      className="custom-switch-check"
                      onChange={(value) => setIfChecked(value)}
                    />
                    <p>
                      {t("LOGIN_AGREE_FIRST")}
                      <a href={TERMS_AND_CONDITIONS} target="__blank">
                        {t("LOGIN_AGREE_LINK")}
                      </a>
                      {t("LOGIN_AGREE_LAST")}
                    </p>
                  </div>
                  {isValidData && (
                    <React.Fragment>
                      <p className="mt-2">{t("AUTH_CODE_MODAL_TEXT")}</p>
                      <InputsCode
                        code={values.code}
                        setCode={(code) => setFieldValue("code", code)}
                        addClass="mt-1 mb-5"
                      />
                    </React.Fragment>
                  )}
                  <div className="invite-page_info __actions">
                    <Button
                      type="submit"
                      loading={loading}
                      name={isValidData ? "ACCEPT" : "LOGIN_BUTTON_CHECK"}
                      disabled={
                        !dirty || !isValid || !ifChecked || !allFieldsFilled || rejecting
                      }
                    />
                    <ButtonRed
                      onClick={handleReject}
                      type="button"
                      name={t("REJECT")}
                      loading={rejecting}
                      disabled={loading}
                    />
                  </div>
                  <div className="df-column-center mt-4">
                    <h4 className="mb-0">{t("LOGIN_PAGE_SUPPORT_HEADER")}</h4>
                    <a
                      href={`${t("LOGIN_PAGE_SUPPORT_LINK_URL")}`}
                      className="cta"
                      target="_BLANK"
                      rel="noreferrer"
                    >
                      {t("LOGIN_PAGE_SUPPORT_LINK_TEXT")}
                    </a>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </div>
    );
  })
);

export default InviteOnBoardingPage;
